import { AnyAction, MiddlewareAPI } from 'redux';
import { ThunkDispatch } from 'store/store';
import { PositionEntryActionType } from './actions';
import { portfolioSetActiveInputActionAsync } from 'portfolio/store/thunks';
import { layoutSetActiveInputAsPortfolioInput } from 'layout/store/thunks';
import {
    FilteredPortfolioChangedEventType,
    actionIsPortfolioSetActiveInputAction,
} from 'portfolio/store/actions';
import { selectAppMode } from 'app/store/general/selectors';
import { AppMode } from 'app/store/general/AppMode';
import State from 'store/State';
import Services from 'store/Services';
import { buildLogger } from '@instinet-analytics/core-lib';
import { dispatchSetPortfolioSelectionIfChanged } from 'portfolio/store/middleware';
import { getPositionEntryPortfolioSelection } from 'positionEntry/PositionEntryPortfolioInput';
import { AppSettingsActionType } from 'app/store/appSettings/actions';
import { selectActiveInput } from 'portfolio/store/selectors';

export const positionEntryMiddleware = (services: Services) => [
    setActiveInputOnPositionEntrySetMiddleware(services),
    reactivatePreviousActiveInputOnPositionEntryClearMiddleware(),
    publishFilteredPortfolioChangedEventOnPositionEntryChangedMiddleware(
        services
    ),
    raiseFilteredPortfolioChangedAsyncOnPositionEntryBecomesActiveInputMiddleware(
        services
    ),
    updatePortfolioSelectionOnPositionEntryChangedMiddleware(services),
    updatePortfolioSelectionOnSymbologyChangedMiddleware(services),
];

export function setActiveInputOnPositionEntrySetMiddleware(services: Services) {
    const { loggingService } = services;
    const logger = buildLogger(
        'PositionEntry setActiveInputOnPositionEntrySetMiddleware',
        loggingService
    );

    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: AnyAction) => {
            next(action);

            if (action.type === PositionEntryActionType.POSITION_ENTRY_SET) {
                logger.debug('Active input changed', action.payload);
                middlewareAPI.dispatch(
                    portfolioSetActiveInputActionAsync('Position Entry')
                );
            }
        };
}

export function reactivatePreviousActiveInputOnPositionEntryClearMiddleware() {
    return (middlewareApi: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: AnyAction) => {
            next(action);

            if (action.type !== PositionEntryActionType.POSITION_ENTRY_CLEAR) {
                return;
            }

            const state = middlewareApi.getState();
            const appMode = selectAppMode(state);

            if (appMode === AppMode.STANDALONE) {
                middlewareApi.dispatch(layoutSetActiveInputAsPortfolioInput());
            } else {
                // Set input back to embedded app...
                middlewareApi.dispatch(
                    portfolioSetActiveInputActionAsync('Embedded App')
                );
            }
        };
}

export function publishFilteredPortfolioChangedEventOnPositionEntryChangedMiddleware(
    services: Services
) {
    return () => (next: ThunkDispatch<AnyAction>) => (action: AnyAction) => {
        next(action);

        const { portfolioEventService } = services;

        if (
            action.type === PositionEntryActionType.POSITION_ENTRY_SET ||
            action.type === PositionEntryActionType.POSITION_ENTRY_CLEAR
        ) {
            portfolioEventService.publishFilteredPortfolioChanged({
                changedType: FilteredPortfolioChangedEventType.RESET,
            });
        }
    };
}

export function updatePortfolioSelectionOnPositionEntryChangedMiddleware(
    services: Services
) {
    return (middlewareApi: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: AnyAction) => {
            next(action);

            const { loggingService } = services;

            if (action.type === PositionEntryActionType.POSITION_ENTRY_SET) {
                loggingService.debug(
                    'store|updatePortfolioSelectionOnPositionEntryChangedMiddleware',
                    'Selection changed required: position entry has been set'
                );

                const nextPortfolioSelection =
                    getPositionEntryPortfolioSelection(
                        middlewareApi.getState()
                    );
                dispatchSetPortfolioSelectionIfChanged(
                    middlewareApi,
                    services,
                    nextPortfolioSelection
                );
            }
        };
}

export function updatePortfolioSelectionOnSymbologyChangedMiddleware(
    services: Services
) {
    return (middlewareApi: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: AnyAction) => {
            next(action);

            if (action.type === AppSettingsActionType.UPDATE) {
                const state = middlewareApi.getState();
                const activeInput = selectActiveInput(state);
                if (activeInput.activeInputType !== 'Position Entry') {
                    return;
                }

                const nextPortfolioSelection =
                    getPositionEntryPortfolioSelection(
                        middlewareApi.getState()
                    );
                dispatchSetPortfolioSelectionIfChanged(
                    middlewareApi,
                    services,
                    nextPortfolioSelection
                );
            }
        };
}

export function raiseFilteredPortfolioChangedAsyncOnPositionEntryBecomesActiveInputMiddleware(
    services: Services
) {
    return () => (next: ThunkDispatch<AnyAction>) => (action: AnyAction) => {
        next(action);

        const { portfolioEventService } = services;

        if (!actionIsPortfolioSetActiveInputAction(action)) {
            return;
        }

        if (action.payload!.activeInputType !== 'Position Entry') {
            return;
        }

        portfolioEventService.publishFilteredPortfolioChanged({
            changedType: FilteredPortfolioChangedEventType.RESET,
        });
    };
}
